import { useState, useCallback, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { ARCHIVE_CHAT_GLOBAL_CONTEXT } from '../utils/archiveChatContext';
import type { ThesconAiScopeConfig } from '../components/ThesconAiScopePanel';
import { db } from '../lib/firebase';
import {
  appendMessage,
  clearMessageRetrievalJob,
  createThread,
  deleteThreadAndMessages,
  subscribeThreadMessages,
  touchThreadAfterMessage,
  type ThreadMessageDoc,
} from '../services/thesconAiThreads';

export type ThesconAiMessage = {
  id: string;
  text: string;
  isUser: boolean;
  /** Live Firestore job for staged retrieval (Thescon AI timeline). */
  retrievalJobId?: string;
};

export type UseThesconAiChatOptions = {
  threadId: string | null;
  onThreadIdChange: (id: string | null) => void;
};

const ASK_CHAT_URL =
  process.env.REACT_APP_ASK_CHAT_URL ||
  process.env.NEXT_PUBLIC_ASK_CHAT_URL ||
  'https://europe-west3-thescon-app.cloudfunctions.net/askChat';

const ASK_KNOWLEDGE_URL =
  process.env.REACT_APP_ASK_KNOWLEDGE_URL ||
  process.env.NEXT_PUBLIC_ASK_KNOWLEDGE_URL ||
  'https://askknowledge-3l53kqimxq-ey.a.run.app';
const ASK_KNOWLEDGE_FALLBACK_URL =
  process.env.REACT_APP_ASK_KNOWLEDGE_FALLBACK_URL ||
  process.env.NEXT_PUBLIC_ASK_KNOWLEDGE_FALLBACK_URL ||
  'https://europe-west3-thescon-app.cloudfunctions.net/askKnowledge';

/** Staged retrieval + Firestore job timeline (same body as askKnowledge + optional jobId). */
const RETRIEVE_KNOWLEDGE_URL =
  process.env.REACT_APP_RETRIEVE_KNOWLEDGE_URL ||
  process.env.NEXT_PUBLIC_RETRIEVE_KNOWLEDGE_URL ||
  'https://europe-west3-thescon-app.cloudfunctions.net/retrieveKnowledge';
const RETRIEVE_KNOWLEDGE_FALLBACK_URL =
  process.env.REACT_APP_RETRIEVE_KNOWLEDGE_FALLBACK_URL ||
  process.env.NEXT_PUBLIC_RETRIEVE_KNOWLEDGE_FALLBACK_URL ||
  RETRIEVE_KNOWLEDGE_URL;

const AI_KNOWLEDGE_USER_ID = 'grdKljB3XuW5iVSqQl3N6VDfvi53';

export function useThesconAiChat(scopeConfig: ThesconAiScopeConfig, options: UseThesconAiChatOptions) {
  const { threadId, onThreadIdChange } = options;
  const [messages, setMessages] = useState<ThesconAiMessage[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentsWithoutChunks, setDocumentsWithoutChunks] = useState<string[]>([]);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      return undefined;
    }
    const unsub = subscribeThreadMessages(db, threadId, (rows) => {
      setMessages(
        rows.map((r) => ({
          id: r.id,
          text: r.doc.content,
          isUser: r.doc.role === 'user',
          retrievalJobId: r.doc.retrievalJobId,
        }))
      );
    });
    return () => unsub();
  }, [threadId]);

  const normalizeEndpoint = (url: string): string => url.replace(/\/+$/, '');
  const shouldFallbackToAskKnowledge = async (response: Response): Promise<boolean> => {
    if (response.status !== 404 && response.status !== 405) return false;
    try {
      const cloned = response.clone();
      const data = (await cloned.json()) as { error?: string };
      const err = (data.error || '').toLowerCase();
      if (err.includes('no document chunks found')) return false;
      if (err.includes('missing required fields')) return false;
      if (err.includes('question too long')) return false;
      if (err.includes('invalid json')) return false;
      if (err.includes('malformed json')) return false;
      return false;
    } catch {
      // Non-JSON 404/405 usually means endpoint mismatch/old deployment.
      return true;
    }
  };

  const sendMessage = useCallback(async () => {
    const trimmedGuard = input.trim();
    if (!trimmedGuard || isThinking) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setError('Please sign in to chat.');
      return;
    }

    const priorMessages = messages;
    const retrievalJobId = crypto.randomUUID();
    const userMessageId = crypto.randomUUID();
    setInput('');
    setIsThinking(true);
    setError(null);

    const useRag = !!(
      scopeConfig.projectId ||
      scopeConfig.knowledgeId ||
      scopeConfig.companyId
    );
    const externalAugmentRequested =
      scopeConfig.agentMode &&
      (scopeConfig.webSearch || scopeConfig.claudeQuery || scopeConfig.openaiQuery);
    const useRetrievalFlow = useRag || externalAugmentRequested;

    let effectiveThreadId: string | null = threadId;

    try {
      if (!effectiveThreadId) {
        const title = trimmedGuard.slice(0, 120) || 'New chat';
        effectiveThreadId = await createThread(db, user.uid, scopeConfig, title);
        onThreadIdChange(effectiveThreadId);
      }

      const tid = effectiveThreadId;
      if (!tid) return;

      await appendMessage(db, tid, userMessageId, {
        role: 'user',
        content: trimmedGuard,
        retrievalJobId: useRetrievalFlow ? retrievalJobId : undefined,
      });
      await touchThreadAfterMessage(db, tid, trimmedGuard, { scope: scopeConfig });

      if (useRetrievalFlow) {
        let usedLegacyKnowledgeFallback = false;
        const contextFromMessages =
          priorMessages.length > 0
            ? priorMessages.map((m) => `${m.isUser ? 'User' : 'Assistant'}: ${m.text}`).join('\n\n')
            : '';
        const additionalContext = [ARCHIVE_CHAT_GLOBAL_CONTEXT, contextFromMessages]
          .filter(Boolean)
          .join('\n\n');

        const payload = {
          userId: AI_KNOWLEDGE_USER_ID,
          askingUserId: user.uid,
          question: trimmedGuard,
          topK: 8,
          jobId: retrievalJobId,
          clientMode: scopeConfig.agentMode ? 'agent' : 'standard',
          model: scopeConfig.model,
          retrievalOptions: scopeConfig.agentMode
            ? undefined
            : {
                enableQueryAnalysis: false,
                enableRerank: false,
                enableCritic: false,
                enableGraphRetrieval: false,
                enableExternalWebSearch: false,
                enableExternalClaude: false,
                enableExternalOpenai: false,
              },
          ...(scopeConfig.agentMode
            ? {
                externalAugment: {
                  webSearch: scopeConfig.webSearch,
                  claude: scopeConfig.claudeQuery,
                  openai: scopeConfig.openaiQuery,
                },
              }
            : {}),
          additionalContext: additionalContext || undefined,
          projectId: scopeConfig.projectId || undefined,
          knowledgeId: scopeConfig.knowledgeId || undefined,
          companyId: scopeConfig.companyId || undefined,
          selectedProjectDocumentIds: scopeConfig.projectId
            ? scopeConfig.selectedProjectDocumentIds.length > 0
              ? scopeConfig.selectedProjectDocumentIds
              : undefined
            : undefined,
          selectedKnowledgeDocumentIds: scopeConfig.knowledgeId
            ? scopeConfig.selectedKnowledgeDocumentIds.length > 0
              ? scopeConfig.selectedKnowledgeDocumentIds
              : undefined
            : undefined,
          selectedCompanyDocumentIds: scopeConfig.companyId
            ? scopeConfig.selectedCompanyDocumentIds.length > 0
              ? scopeConfig.selectedCompanyDocumentIds
              : undefined
            : undefined,
        };
        const retrievePrimary = normalizeEndpoint(RETRIEVE_KNOWLEDGE_URL);
        const retrieveFallback = normalizeEndpoint(RETRIEVE_KNOWLEDGE_FALLBACK_URL);
        let response = await fetch(retrievePrimary, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if ((response.status === 404 || response.status === 405) && retrieveFallback !== retrievePrimary) {
          response = await fetch(retrieveFallback, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        }

        if (await shouldFallbackToAskKnowledge(response)) {
          usedLegacyKnowledgeFallback = true;
          await clearMessageRetrievalJob(db, tid, userMessageId);
          const { jobId: _stripJob, ...askPayload } = payload;
          void _stripJob;
          const primaryUrl = normalizeEndpoint(ASK_KNOWLEDGE_URL);
          const fallbackUrl = normalizeEndpoint(ASK_KNOWLEDGE_FALLBACK_URL);
          response = await fetch(primaryUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(askPayload),
          });
          if ((response.status === 404 || response.status === 405) && fallbackUrl !== primaryUrl) {
            response = await fetch(fallbackUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(askPayload),
            });
          }
        }

        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
          answer?: string;
          documentsWithoutChunks?: string[];
        };

        if (!response.ok) {
          setDocumentsWithoutChunks(data.documentsWithoutChunks ?? []);
          throw new Error(data.error || `Request failed: ${response.status}`);
        }

        const answer = data.answer;
        setDocumentsWithoutChunks(data.documentsWithoutChunks ?? []);
        if (answer) {
          const assistantPayload: Omit<ThreadMessageDoc, 'createdAt'> = {
            role: 'assistant',
            content: answer,
          };
          await appendMessage(db, tid, crypto.randomUUID(), assistantPayload);
          await touchThreadAfterMessage(db, tid, answer.slice(0, 280), {
            scope: scopeConfig,
          });
          if (usedLegacyKnowledgeFallback) {
            const fallbackNote = externalAugmentRequested
              ? 'Note: retrieval timeline/external query steps were skipped because retrieveKnowledge is unavailable (fallback to askKnowledge).'
              : 'Note: retrieval timeline was skipped because retrieveKnowledge is unavailable (fallback to askKnowledge).';
            await appendMessage(db, tid, crypto.randomUUID(), {
              role: 'assistant',
              content: fallbackNote,
            });
          }
        } else {
          throw new Error('No answer from knowledge service.');
        }
      } else {
        const conversationHistory = priorMessages.map((msg) => ({
          role: (msg.isUser ? 'user' : 'assistant') as 'user' | 'assistant',
          content: msg.text,
        }));

        const response = await fetch(ASK_CHAT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmedGuard,
            conversationHistory,
            askingUserId: user.uid,
            model: scopeConfig.model,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || `Request failed: ${response.status}`);
        }

        const result = (await response.json()) as { answer?: string };
        const answer = result.answer;
        if (answer) {
          await appendMessage(db, tid, crypto.randomUUID(), {
            role: 'assistant',
            content: answer,
          });
          await touchThreadAfterMessage(db, tid, answer.slice(0, 280), { scope: scopeConfig });
        } else {
          throw new Error('No answer from assistant.');
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      setError(message);
      const tid = effectiveThreadId;
      if (tid) {
        await appendMessage(db, tid, crypto.randomUUID(), {
          role: 'assistant',
          content: message,
        });
        await touchThreadAfterMessage(db, tid, message.slice(0, 280), { scope: scopeConfig });
      }
    } finally {
      setIsThinking(false);
    }
  }, [input, isThinking, messages, scopeConfig, threadId, onThreadIdChange]);

  const clearMessages = useCallback(async () => {
    if (threadId) {
      try {
        await deleteThreadAndMessages(db, threadId);
      } catch (e) {
        console.error('[useThesconAiChat] failed to delete thread', e);
      }
      onThreadIdChange(null);
    }
    setMessages([]);
    setError(null);
    setDocumentsWithoutChunks([]);
  }, [threadId, onThreadIdChange]);

  return {
    messages,
    input,
    setInput,
    sendMessage,
    isThinking,
    error,
    clearMessages,
    documentsWithoutChunks,
  };
}
